import React, { useState, useEffect, useMemo } from 'react';
import axios from "../../../../axios-order";
import { Snackbar, Alert } from '@mui/material';

function Seven({ handleBack, updateTableData, data, reasonsData, title, get_gstr9_report_data }) {
  // --- INDEXES MATCHING YOUR ACTUAL BACKEND DATA ---
  const INDEX_A = 0;
  const INDEX_B = 1;
  const INDEX_C = 2;
  const INDEX_D = 3;
  const INDEX_D1 = 4; // <-- THIS ROW IS EDITABLE
  const INDEX_E = 5; // Auto calculated
  const INDEX_F = 6;
  const INDEX_G = 7; // Auto calculated (F - E)

  const [reasonText, setReasonText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [manualInputValues, setManualInputValues] = useState([]);
  const [d1TaxableValue, setD1TaxableValue] = useState('');

  // Sync data & reasons from parent
  useEffect(() => {
    if (data) {
      setManualInputValues(data.map(item => item.manual_input ?? ''));
      const backendD1 = data[INDEX_D1]?.taxable_non_taxable_value;
      setD1TaxableValue(backendD1 != null ? String(backendD1) : '');
    }
    setReasonText(reasonsData?.[0]?.reasons || '');
  }, [data, reasonsData]);

  // Auto calculate Row E = A - B - C - D - D1
  const rowEValue = useMemo(() => {
    if (!data) return 0;
    const A = parseFloat(data[INDEX_A]?.taxable_non_taxable_value) || 0;
    const B = parseFloat(data[INDEX_B]?.taxable_non_taxable_value) || 0;
    const C = parseFloat(data[INDEX_C]?.taxable_non_taxable_value) || 0;
    const D = parseFloat(data[INDEX_D]?.taxable_non_taxable_value) || 0;
    const D1 = parseFloat(d1TaxableValue) || 0;
    return parseFloat((A - B - C - D - D1).toFixed(2));
  }, [data, d1TaxableValue]);

  // Auto calculate Row G = F - E
  const rowGValue = useMemo(() => {
    if (!data) return 0;
    const F = parseFloat(data[INDEX_F]?.taxable_non_taxable_value) || 0;
    return parseFloat((F - rowEValue).toFixed(2));
  }, [data, rowEValue]);

  // Push D1, E and G values back to parent
  useEffect(() => {
    if (!data || !updateTableData) return;
    const newData = [...data];
    let changed = false;

    const parsedD1 = d1TaxableValue === '' ? null : parseFloat(d1TaxableValue);
    if (newData[INDEX_D1].taxable_non_taxable_value !== parsedD1) {
      newData[INDEX_D1].taxable_non_taxable_value = parsedD1;
      changed = true;
    }
    if (newData[INDEX_E].taxable_non_taxable_value !== rowEValue) {
      newData[INDEX_E].taxable_non_taxable_value = rowEValue;
      changed = true;
    }
    if (newData[INDEX_G].taxable_non_taxable_value !== rowGValue) {
      newData[INDEX_G].taxable_non_taxable_value = rowGValue;
      changed = true;
    }
    if (changed) updateTableData(newData);
  }, [d1TaxableValue, rowEValue, rowGValue, data, updateTableData]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async () => {
    const payload = {
      business_entity_id: localStorage.getItem("business_entity_id"),
      gstr9c_recon_id: localStorage.getItem("gstr9c_recon_id"),
      records: {
        "7.Reconciliation of Taxable Turnover*": data.map((item, idx) => {
          const manual = manualInputValues[idx] === '' ? null : parseFloat(manualInputValues[idx]);
          if (idx === INDEX_D1) {
            return { ...item, manual_input: manual, taxable_non_taxable_value: d1TaxableValue === '' ? null : parseFloat(d1TaxableValue) };
          }
          if (idx === INDEX_E) return { ...item, manual_input: manual, taxable_non_taxable_value: rowEValue };
          if (idx === INDEX_G) return { ...item, manual_input: manual, taxable_non_taxable_value: rowGValue };
          return { ...item, manual_input: manual };
        }),
        "8.Reasons for Un - Reconciled difference in Taxable Turnover": [{
          description: "Reasons for Un - Reconciled difference in Taxable Turnover",
          reasons: reasonText === '' ? null : reasonText
        }]
      }
    };

    try {
      const res = await axios.post("/gstr9c_manual_data_update", payload);
      setSnackbar({ open: true, message: res.data.message || 'Saved!', severity: res.data.status ? 'success' : 'error' });
      if (res.data.status) get_gstr9_report_data?.();
    } catch (err) {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    }
  };

  // Unified handler for both D1 and Manual Inputs
  const handleInputChange = (setter, currentValues, setValues, index = null) => (e) => {
    let clean = e.target.value.replace('₹', '').trim();

    if (clean === '') {
      if (index !== null) {
        const newVals = [...currentValues];
        newVals[index] = '';
        setValues(newVals);
      } else {
        setter('');
      }
      return;
    }

    // Ensure minus is only at start and only one
    if (clean.includes('-')) {
      clean = '-' + clean.replace(/-/g, '');
    }

    if (/^-?\d*\.?\d{0,2}$/.test(clean)) {
      if (index !== null) {
        const newVals = [...currentValues];
        newVals[index] = clean;
        setValues(newVals);

        // Update parent data for manual inputs
        const parsed = clean === '' ? null : parseFloat(clean);
        const newData = [...data];
        newData[index].manual_input = parsed;
        updateTableData?.(newData);
      } else {
        setter(clean);
      }
    }
  };

  const handleD1TaxableValueChange = handleInputChange(setD1TaxableValue, null, null);
  const handleManualInputChange = (index) => handleInputChange(null, manualInputValues, setManualInputValues, index);

  // Unified KeyDown handler
  const handleKeyDown = (e) => {
    const disabledIndexes = [15, 17];
    if (disabledIndexes.includes(e.target.dataset.index)) {
      e.preventDefault();
      return;
    }

    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];
    if (allowedKeys.includes(e.key)) return;

    const input = e.target;
    const rawValue = input.value;
    const cleanValue = rawValue.replace('₹', '').trim();
    const cursorPos = input.selectionStart;

    // Decimal point: allow only one
    if (e.key === '.') {
      if (cleanValue.includes('.')) e.preventDefault();
      return;
    }

    // Minus sign: allow only at start (position 0 or 1) and only once
    if (e.key === '-') {
      const isAtStart = cursorPos === 0 || cursorPos === 1;
      const hasMinus = cleanValue.startsWith('-');

      if (isAtStart && !hasMinus) {
        return; // allow
      } else {
        e.preventDefault();
      }
      return;
    }

    e.preventDefault();
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = '#88d8a3';
    e.target.style.boxShadow = '0 0 0 2px rgba(136,216,163,0.1)';

    const val = e.target.value;
    const pos = val.startsWith('₹') ? 1 : 0;
    setTimeout(() => e.target.setSelectionRange(pos, pos), 0);
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = '#e2e8f0';
    e.target.style.boxShadow = 'none';
  };

  const formatValue = (value) => {
    if (value == null || value === '') return '0';
    const n = parseFloat(value);
    if (isNaN(n)) return '0';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getDisplayValue = (idx) => {
    if (idx === INDEX_D1) return d1TaxableValue;
    if (idx === INDEX_E) return rowEValue;
    if (idx === INDEX_G) return rowGValue;
    return data?.[idx]?.taxable_non_taxable_value ?? 0;
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '30px', width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
        <button onClick={handleBack} style={{ marginBottom: '0px', padding: '8px 16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: '#64748b' }}>
          Back
        </button>
        <h2 style={{ marginBottom: '20px', color: '#1e293b', fontSize: '20px', fontWeight: '700', textAlign: 'center' }}>
          {title || 'Table 7 - Reconciliation of Gross Turnover'}
        </h2>

        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '60px 2fr 1fr 1fr', gap: '10px', fontWeight: '600', color: '#475569', padding: '15px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '14px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '10px', position: 'sticky', top: 0, zIndex: 1 }}>
          <span style={{ textAlign: 'center' }}>S.No.</span>
          <span>Description</span>
          <span style={{ textAlign: 'center' }}>Taxable/Non-Taxable Value</span>
          <span style={{ textAlign: 'center' }}>Manual Input</span>
        </div>

        {/* Body */}
        <div style={{ maxHeight: 'calc(100vh - 450px)', overflowY: 'scroll', marginTop: '10px', scrollbarWidth: 'thin', scrollbarColor: '#64748b #f1f5f9' }}>
          <style>{`
            div::-webkit-scrollbar { width: 12px; }
            div::-webkit-scrollbar-track { background: #f1f5f9; borderRadius: 6px; }
            div::-webkit-scrollbar-thumb { background: #64748b; borderRadius: 6px; }
            div::-webkit-scrollbar-thumb:hover { background: #475569; }
          `}</style>

          {data && data.length > 0 ? data.map((item, index) => {
            const isD1 = index === INDEX_D1;
            const isE = index === INDEX_E;
            const isG = index === INDEX_G;
            const getRowColor = (i) => ['#e0e7ff', '#e9d5ff', '#f3e8ff', '#ede9fe', '#ddd6fe'][i % 5];

            return (
              <div key={index} style={{
                display: 'grid',
                gridTemplateColumns: '60px 2fr 1fr 1fr',
                gap: '10px',
                alignItems: 'center',
                padding: '10px',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                borderRadius: '6px',
                marginBottom: '2px',
                minHeight: '40px'
              }}>
                <span style={{ color: '#1e293b', fontSize: '13px', fontWeight: '600', textAlign: 'center', padding: '6px', backgroundColor: getRowColor(index), borderRadius: '6px' }}>
                  {index + 1}
                </span>
                <span style={{
                  color: isE || isG ? '#9d3a3a' : '#334155',
                  fontSize: '13px',
                  padding: '6px 10px',
                  backgroundColor: isE || isG ? '#fee2e2' : '#f1f5f9',
                  borderRadius: '6px',
                  wordBreak: 'break-word',
                  fontWeight: isE || isG ? 'bold' : 'normal'
                }}>
                  {item.description || 'N/A'}
                </span>

                {/* Taxable Value Column */}
                <div style={{ textAlign: 'center' }}>
                  {isD1 ? (
                    <input
                      type="text"
                      value={d1TaxableValue !== '' ? `₹${d1TaxableValue}` : ''}
                      onChange={handleD1TaxableValueChange}
                      onKeyDown={handleKeyDown}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={{
                        color: '#1e293b', fontWeight: '500', fontSize: '13px', textAlign: 'right',
                        padding: '6px', border: '1px solid #fd9853', backgroundColor: '#fff7ed',
                        borderRadius: '6px', outline: 'none', width: '100%'
                      }}
                    />
                  ) : (
                    <span style={{
                      color: isE || isG ? '#9d3a3a' : '#1e293b',
                      fontWeight: isE || isG ? 'bold' : '500',
                      fontSize: '13px', textAlign: 'right', padding: '6px',
                      backgroundColor: isE || isG ? '#ffe4e6' : '#ecfdf5',
                      borderRadius: '6px', display: 'block'
                    }}>
                      ₹{formatValue(getDisplayValue(index))}
                    </span>
                  )}
                </div>

                {/* Manual Input Column */}
                <input
                  type="text"
                  value={manualInputValues[index] !== '' ? `₹${manualInputValues[index]}` : ''}
                  onChange={handleManualInputChange(index)}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  data-index={index} // for disabled check
                  style={{
                    color: '#1e293b', fontWeight: '500', fontSize: '13px', textAlign: 'right',
                    padding: '6px', border: '1px solid #e2e8f0', backgroundColor: '#ecfdf5',
                    borderRadius: '6px', outline: 'none', width: '100%'
                  }}
                />
              </div>
            );
          }) : (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>No data available.</div>
          )}
        </div>

        {/* Footer */}
        {data && data.length > 0 && (
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', borderTop: '2px solid #e2e8f0' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b', textAlign: 'center' }}>
              Total Records: {data.length}
            </p>
          </div>
        )}

        {/* Reasons & Submit */}
        <div style={{ marginTop: '30px', display: 'flex', alignItems: 'center', height: '80px', gap: '0' }}>
          <div style={{
            position: 'relative', width: '50%', height: '40px',
            background: 'linear-gradient(135deg, rgb(187,63,25) 0%, rgb(157,58,58) 50%, rgb(127,205,205) 100%)',
            clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 25px rgba(168,230,207,0.3)', overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: 0, right: '10px', width: '40px', height: '100%',
              background: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'%3E%3Cdefs%3E%3Cpattern id=\'pattern2\' x=\'0\' y=\'0\' width=\'15\' height=\'15\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'7.5\' cy=\'7.5\' r=\'1.5\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23pattern2)\'/%3E%3C/svg%3E")',
              zIndex: 1
            }}></div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)', zIndex: 2, marginRight: '20px' }}>
              8. Reasons for Un - Reconciled difference in Taxable Turnover
            </div>
          </div>
          <div style={{ width: '50%', height: '80px', display: 'flex', alignItems: 'center', padding: '0 20px', backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderLeft: 'none', gap: '10px' }}>
            <textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Enter additional notes or remarks..."
              style={{ flex: 1, padding: '10px 15px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white', outline: 'none', resize: 'none', height: '60px' }}
              onFocus={e => { e.target.style.borderColor = '#88d8a3'; e.target.style.boxShadow = '0 0 0 2px rgba(136,216,163,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
            <button onClick={handleSubmit} style={{
              padding: '10px 20px', background: 'linear-gradient(135deg,#ffd3a5 0%,#fd9853 50%,#e8a87c 100%)',
              color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(255,211,165,0.3)', transition: 'all 0.2s ease'
            }}
              onMouseOver={e => e.target.style.transform = 'translateY(-1px)'}
              onMouseOut={e => e.target.style.transform = 'translateY(0)'}>
              Submit
            </button>
          </div>
        </div>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default Seven;